import React, { useEffect, useRef, useState } from 'react';
import { AppContext } from '../../App';
import { BetsExpandCollapseContext, useBetsExpandCollapseContext } from '../../common/BetsExpandCollapseContext';
import ExpandedViewProvider from '../../common/ExpandedViewProvider';
import Loader from "../../common/Loader";
import MessageInfo from "../../common/MessageInfo";
import { getAllAgg } from '../../data/utils/getAllAgg';
import { useFetchMatch } from "../../data/fetchMatch";
import { classIf } from '../../data/utils/classIf';
import { getConfiguration } from '../../data/utils/configuration';
import { getPrincipaleOPrimoGruppo } from '../../data/utils/getPrincipaleOPrimoGruppo';
import { labels } from "../../data/labels";
import { isOverlaySection } from '../../utils/sport-effects';
import LiveMatch from "./LiveMatch";
import LiveMatchHeader from './LiveMatchHeader';
import LiveMatchNavbar from "./LiveMatchNavbar";

function getDefaultAgg(match) {
    const agg = getAllAgg(true, match.sportRef);
    return agg.idAggregata;
}

export const LiveMatchContext = React.createContext(null);

function LiveMatchDataFetcher(props) {
    const conf = getConfiguration();
    const appContext = React.useContext(AppContext);
    const [betsExpandCollapseContext] = useBetsExpandCollapseContext();

    const firstRender = useRef(true);

    const match = props.match;

    let initState = (() => {
        if (firstRender.current === true) {
            firstRender.current = false;
        } else {
            return;
        }

        let favorites = localStorage.getItem('favorite_markets');
        if (favorites != null) {
            favorites = JSON.parse(favorites);
        } else {
            favorites = {};
        }

        if (favorites[match.sportRef] !== undefined) {
            return {
                match: null,
                data: null,
                matchFetch: match,
                aggregata: null,
                aggregataFetch: getAllAgg(true, match.sportRef).idAggregata,
                showLoader: true,
                landOnFavorites: true,
                setFavoritesAsSelected: true,
                favoritesSelected: false,
                favorites
            };
        } else {
            return {
                match: null,
                data: null,
                matchFetch: match,
                aggregata: null,
                aggregataFetch: getDefaultAgg(match),
                showLoader: true,
                landOnFavorites: false,
                setFavoritesAsSelected: null,
                favoritesSelected: false,
                favorites
            };
        }
    })();

    const [state, setState] = useState({
        ...initState,
        favorites: (() => {
            const favorites = localStorage.getItem('favorite_markets');
            if (favorites != null) {
                return JSON.parse(favorites);
            } else {
                return {};
            }
        })(),
        update(updt) {
            setState(prev => {
                return {...prev, ...updt};
            });
        },
        setData(data) {
            if (this.setFavoritesAsSelected != null) {
                if (this.setFavoritesAsSelected === true) {
                    // I check whether there's any bets matching the favorite markets
                    let favorites = [];
                    try {
                        favorites = data.bets.filter(bet =>
                            bet.hasOdds && this.isMarketFavorite(this.matchFetch.sportRef, `${bet.id}_${bet.handicap}`)
                        );
                    } catch (e) {
                        console.error(e);
                    }
                    // If there are none, I show to the main bet group as selected by filtering only the bets that don't belong to it
                    if (this.landOnFavorites && favorites.length === 0) {
                        this.update({showLoader: false, data, aggregata: this.aggregataFetch, match: this.matchFetch, landOnFavorites: false, favoritesSelected: false, setFavoritesAsSelected: null});
                    } else {
                        this.update({showLoader: false, data, aggregata: this.aggregataFetch, match: this.matchFetch, landOnFavorites: false, favoritesSelected: true, setFavoritesAsSelected: null});
                    }
                } else {
                    this.update({showLoader: false, data, aggregata: this.aggregataFetch, match: this.matchFetch, landOnFavorites: false, favoritesSelected: false, setFavoritesAsSelected: null});
                }
            } else {
                if (data.gruppoById?.[this.aggregataFetch] == null) {
                    const ag = getPrincipaleOPrimoGruppo(data.gruppiScommessa);
                    this.update({showLoader: false, data, aggregataFetch: ag.idAggregata, aggregata: ag.idAggregata, match: this.matchFetch});
                } else {
                    this.update({showLoader: false, data, aggregata: this.aggregataFetch, match: this.matchFetch});
                }
            }
        },
        setAggregataFetch(aggregataFetch) {
            if (aggregataFetch === this.aggregata) {
                this.update({favoritesSelected: false});
            } else {
                this.update({aggregataFetch, setFavoritesAsSelected: false});
            }
        },
        changeMatch(matchFetch) {
            if (this.showFavoritesForSport(matchFetch.sportRef)) {
                const aggregataFetch = this.data.gruppiScommessa.find(ag => ag.idAggregata < 0).idAggregata;
                this.update({aggregataFetch, matchFetch, landOnFavorites: true, setFavoritesAsSelected: true});
            } else {
                const aggregataFetch = getDefaultAgg(matchFetch);
                this.update({aggregataFetch, matchFetch, setFavoritesAsSelected: false});
            }
        },
        setShowLoader: function(showLoader) {
            this.update({showLoader});
        },
        favoriteMarketsOn() {
            const aggregataFetch = this.data.gruppiScommessa.find(ag => ag.idAggregata < 0).idAggregata;
            if (this.aggregata === aggregataFetch) {
                this.update({favoritesSelected: true});
            } else {
                this.update({aggregataFetch, setFavoritesAsSelected: true});
            }
        },
        favoriteMarketsOff() {
            this.update({favoritesSelected: false});
        },
        toggleFavorite(sportId, betId) {
            if (this.favorites[sportId] === undefined) {
                this.favorites[sportId] = {};
                this.favorites[sportId][betId] = true;
            } else {
                if (this.favorites[sportId][betId] === undefined) {
                    this.favorites[sportId][betId] = true;
                } else {
                    delete this.favorites[sportId][betId];
                    if (Object.keys(this.favorites[sportId]).length === 0) {
                        delete this.favorites[sportId];
                    }
                }
            }
            if (Object.keys(this.favorites).length > 0) {
                this.update({favorites: {...this.favorites}});
                localStorage.setItem('favorite_markets', JSON.stringify(this.favorites));
            } else {
                this.update({favorites: {}, favoritesSelected: false});
                localStorage.removeItem('favorite_markets');
            }
        },
        showFavoritesForSport(sportId) {
            return this.favorites[sportId] !== undefined;
        },
        isMarketFavorite(sportId, betId) {
            return this.favorites[sportId] !== undefined && this.favorites[sportId][betId] !== undefined;
        }
    });

    useEffect(() => {
        if (state.match != null) {
            state.changeMatch(match);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [match]);

    const [request, , isSetInterval] = useFetchMatch(state.matchFetch, state.aggregataFetch, true, false, true);

    useEffect(() => {
        let timeout = null;
        if (request.isLoading) {
            timeout = setTimeout(function() {
                state.setShowLoader(true);
            }, 100);
        } else {
            clearTimeout(timeout);
            if (!request.isError) {
                state.setData(request.data);
            } else {
                state.setShowLoader(true);
            }
        }

        return () => {
            clearTimeout(timeout);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request]);

    useEffect(() => {
        betsExpandCollapseContext.resetBetsCollapsed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.aggregataFetch]);

    function changeAggregata(aggregata) {
        state.setAggregataFetch(aggregata);       
    }
    
    const hideWrapper = isOverlaySection(appContext,props.section);
    const stickyWidget = !conf.wl;

    return (
        ((!isSetInterval && state.showLoader) || request.isError ?
            <Loader error={request.isError} hide={appContext.showBetslip} />
            :
            <LiveMatchContext.Provider value={state}>
                <BetsExpandCollapseContext.Provider value={betsExpandCollapseContext}>
                    <ExpandedViewProvider>
                        <div id="live-match">
                            {stickyWidget ?
                                <div className={hideWrapper ? 'no-scroll d-none' : ''}>
                                    <LiveMatchHeader tournaments={props.tournaments} match={state.match} aggregata={state.aggregataFetch} />
                                </div>
                                :
                                <div className={"sticky" + classIf(hideWrapper, 'no-scroll d-none')}>
                                    <LiveMatchHeader tournaments={props.tournaments} match={state.match} aggregata={state.aggregataFetch} />
                                    <LiveMatchNavbar  match={state.match} giocabilita={state.data.giocabilita} gruppiScommessa={state.data.gruppiScommessa} aggregata={state.aggregataFetch} changeAggregata={changeAggregata} tournaments={props.tournaments} />
                                </div>
                            }
                            <div id="main-wrapper" className={"wrapper" + classIf(hideWrapper, 'no-scroll d-none')}>
                                <div id="main-container" className="main-content">
                                    {stickyWidget &&
                                        <LiveMatchNavbar match={state.match} giocabilita={state.data.giocabilita} gruppiScommessa={state.data.gruppiScommessa} aggregata={state.aggregataFetch} changeAggregata={changeAggregata} tournaments={props.tournaments} stickyWidget={stickyWidget} />
                                    }
                                    {!state.data.giocabilita ?
                                        <MessageInfo message={labels['general.giocabilita_chiusa']} hide={appContext.showBetslip} />
                                        :
                                        (!isSetInterval && state.showLoader) || request.isError ?
                                            <Loader error={request.isError} hide={appContext.showBetslip} />
                                            :
                                            <LiveMatch {...state.match} {...state.data} aggregata={state.aggregata} />
                                    }
                                </div>
                            </div>
                        </div>
                    </ExpandedViewProvider>
                </BetsExpandCollapseContext.Provider>
            </LiveMatchContext.Provider>
        )
    );

}

export default LiveMatchDataFetcher;